import React from 'react';
import Linkify from 'react-linkify';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import SvgIcon from '@material-ui/core/SvgIcon';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import classnames from 'classnames';
import {
  STUDENTS_ASSIGNMENTS_DETAIL_URL,
  STUDENTS_ASSIGNMENTS_LIST_URL
} from 'config/urls';
import { filter, first, get, isEmpty, isNull, map, size } from 'lodash';
import {
  fetchAssignmentIsMath,
  studentAssignmentChatbotWarmup,
  studentAssignmentEmotionUpdate,
  studentHasEmotionForAssignment
} from 'sdk';

import {
  StatusOptions,
  TaskInfo,
  Typography,
  withNavbar
} from 'pages/Students/shared';
import { ReactComponent as CameraIcon } from 'pages/Students/shared/assets/camera.svg';
import { colors } from 'theme/palette';
import { formatDate } from 'utils/moment';
import { notifyErrors } from 'utils/notifications';
import { openInNewTab, parseParams, stringifyParams } from 'utils/urls';
import { reverse } from 'utils/urls';

import CheckEmotionalStateDialog from './components/CheckEmotionalStateDialog';
import { MagicLoginDialog } from './components/MagicLoginDialog';
import Button from 'components/Button';
import UserContext from 'components/UserContext';

import { ReactComponent as FileIcon } from './assets/file.svg';
import { ReactComponent as PhoneIcon } from './assets/phone.svg';
import { assignmentDetail, generateMagicLoginToken } from './sdk';
import styles from './styles.module.scss';

const StyledLinearProgress = withStyles({
  root: {
    background: 'var(--white)',
    borderRadius: 4,
    height: 6
  },
  bar: {
    background: 'var(--light-blue)'
  }
})(LinearProgress);

const AssignmentInfo = ({
  assignment,
  onTakePhotoClick,
  generateMagicLoginToken,
  lgScreen
}) => {
  const completeTasks = size(
    filter(
      assignment.tasks,
      ({ status }) =>
        status === StatusOptions.COMPLETE || status === StatusOptions.SUBMITTED
    )
  );

  return (
    <div className={styles.assignmentInfoContainer}>
      <div className={styles.assignmentInfo}>
        <div>
          <Typography variant="H-TEXT-3" color={colors.white}>
            {size(assignment.tasks)} TASKS
          </Typography>
          <Typography variant="B-Text-3" color={colors.white}>
            Due {formatDate(assignment.due_at, 'ddd MM/D [at] h:mm a')}
          </Typography>
        </div>
        <div>
          <Typography variant="S-TEXT-2" color={colors.blue5}>
            {completeTasks} / {size(assignment.tasks)} Done
          </Typography>
          <StyledLinearProgress
            variant="determinate"
            value={(completeTasks / size(assignment.tasks)) * 100}
          />
        </div>
      </div>
      <Typography
        variant={lgScreen ? 'B-Text-2' : 'B-Text-3'}
        className={styles.description}
        color={colors.white}
      >
        <Linkify properties={{ target: '_blank' }}>
          {assignment.description}
        </Linkify>
      </Typography>
      {!isEmpty(assignment.attachments) && (
        <div>
          {map(assignment.attachments, (attachment) => (
            <div
              className={classnames(styles.attachment, 'pointer')}
              key={attachment.id}
              onClick={() => openInNewTab(attachment.file_url)}
            >
              <FileIcon />
              <Typography variant="B-Text-3" color={colors.blue5}>
                {attachment.original_filename}
              </Typography>
            </div>
          ))}
        </div>
      )}
      <Grid container justifyContent="center">
        <Grid item>
          <Button
            onClick={onTakePhotoClick}
            color="lightGreen"
            className={styles.goButton}
            startIcon={
              <CameraIcon
                className={styles.cameraButtonIcon}
                fill={colors.white}
              />
            }
          >
            GO
          </Button>
          {lgScreen && (
            <Button
              onClick={generateMagicLoginToken}
              color="pink"
              className={styles.phoneButton}
              startIcon={
                <SvgIcon component={PhoneIcon} htmlColor={colors.white} />
              }
            >
              PHONE
            </Button>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

class StudentAssignmentsOverview extends React.Component {
  static contextType = UserContext;

  state = {
    loading: true,
    assignment: null,
    magicLoginDialog: {
      open: false,
      token: null
    },
    hasEmotion: null,
    isMathAssignment: null
  };

  componentDidMount() {
    this.fetchAssignment();

    studentAssignmentChatbotWarmup();

    const { backUrl } = parseParams(
      get(this.props, 'history.location.search', '')
    );

    if (!isEmpty(backUrl)) {
      // If a back url is provided we use it for the back action.
      this.props.navigation.setBackButtonAction(() =>
        this.props.history.push(backUrl)
      );
    } else {
      this.props.navigation.setBackButtonAction(this.goToAssignmentsList);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.assignment && prevState.assignment === null) {
      this.fetchStudentHasEmotionForAssignment();
      this.fetchAssignmentType();
    }
  }

  fetchAssignment = async () => {
    const { assignmentId } = this.props.match.params;
    const { student_id: studentId } = this.context.user;

    const {
      data: assignment,
      success,
      errors
    } = await assignmentDetail(studentId, assignmentId);

    if (success) {
      this.setState({ assignment, loading: false });
      this.props.navigation.setTitle(assignment.name);
    } else {
      this.setState({ loading: false, assignment: null }, () =>
        notifyErrors(errors)
      );
      this.props.navigation.setTitle(null);
    }
  };

  goToAssignmentsList = () =>
    this.props.history.push(STUDENTS_ASSIGNMENTS_LIST_URL);

  goToAssignmentDetail = (taskId, camera) => {
    const assignmentId = get(this.props, 'match.params.assignmentId');

    this.props.history.push({
      pathname: reverse(STUDENTS_ASSIGNMENTS_DETAIL_URL, {
        assignmentId
      }),
      search: stringifyParams({ task: taskId, camera })
    });
  };

  generateMagicLoginToken = async () => {
    const postData = {
      redirect_path:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        this.props.history.location.pathname
    };

    const { data, success, errors } = await generateMagicLoginToken(postData);

    if (success) {
      this.setState({
        magicLoginDialog: {
          open: true,
          token: data.key
        }
      });
    } else {
      notifyErrors(errors);
    }
  };

  closeMagicLoginDialog = () => {
    this.setState({
      magicLoginDialog: {
        ...this.state.magicLoginDialog,
        open: false
      }
    });
  };

  fetchStudentHasEmotionForAssignment = async () => {
    const { student_id: studentId } = this.context.user;
    const { assignment } = this.state;

    if (!assignment?.id) return;

    const {
      data: emotionData,
      success,
      errors
    } = await studentHasEmotionForAssignment({
      studentId,
      trackerInstanceSectionId: assignment.id
    });

    if (success) {
      this.setState({ hasEmotion: emotionData.has_student_emotion });
    } else {
      notifyErrors(errors);
    }
  };

  fetchAssignmentType = async () => {
    const { assignment } = this.state;
    const { student_id: studentId } = this.context.user;

    if (!assignment?.id) return;

    const {
      data: assignmentType,
      success,
      errors
    } = await fetchAssignmentIsMath({
      studentId: studentId,
      trackerInstanceSectionId: assignment.id
    });

    if (success) {
      this.setState({ isMathAssignment: assignmentType.is_math });
      if (assignmentType.is_math) {
        this.setState({ isChatOpen: true });
      }
    } else {
      this.setState({ loading: false, emberDetails: null }, () =>
        notifyErrors(errors)
      );
    }
  };

  handleStudentEmotionSelect = async (student_emotion) => {
    const { student_id: studentId } = this.context.user;
    const { assignment } = this.state;

    if (!assignment.id) return;

    const { success, errors } = await studentAssignmentEmotionUpdate({
      studentId,
      trackerInstanceSectionId: assignment.id,
      student_emotion
    });

    if (success) {
      this.setState({ hasEmotion: true });
    } else {
      notifyErrors(errors);
    }
  };

  render() {
    const {
      assignment,
      loading,
      magicLoginDialog,
      hasEmotion,
      isMathAssignment
    } = this.state;

    const lgScreen = isWidthUp('sm', this.props.width);

    if (loading || isNull(hasEmotion) || isNull(isMathAssignment)) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <CircularProgress color="secondary" />
          Loading...
        </Box>
      );
    }

    if (!assignment) {
      return <h3>Assignment does not exist.</h3>;
    }

    return (
      <div className={styles.content}>
        <AssignmentInfo
          lgScreen={lgScreen}
          assignment={assignment}
          onTakePhotoClick={() =>
            this.goToAssignmentDetail(get(first(assignment.tasks), 'id'), true)
          }
          generateMagicLoginToken={this.generateMagicLoginToken}
        />
        <div className={styles.tasksContainer}>
          {map(assignment.tasks, (task, index) => (
            <TaskInfo
              key={task.id}
              lgScreen={lgScreen}
              onClick={() => this.goToAssignmentDetail(task.id, false)}
              index={index + 1}
              task={task}
            />
          ))}
        </div>
        {magicLoginDialog.open && (
          <MagicLoginDialog
            token={magicLoginDialog.token}
            closeDialog={this.closeMagicLoginDialog}
          />
        )}
        {hasEmotion === false && isMathAssignment && (
          <CheckEmotionalStateDialog
            onClick={this.handleStudentEmotionSelect}
          />
        )}
      </div>
    );
  }
}

export default withWidth()(withNavbar(StudentAssignmentsOverview));
